import React, { forwardRef, memo, useMemo } from 'react';
import classNames from 'classnames';
import _has from 'lodash/has';
import _some from 'lodash/some';
import _filter from 'lodash/filter';
import { v4 as uuidv4 } from 'uuid';
import CheckBox from '@lib/components/CheckBox/CheckBox';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { Option } from '@lib/interfaces/form';
import styles from './CheckBoxGroup.module.scss';

export enum CheckBoxWrapViews {
  row = 'row',
  list = 'list',
  calendar = 'calendar',
  monthList = 'monthList',
}

export interface CheckBoxGroupProps {
  CheckBoxComponent?: React.JSX.ElementType;
  checkBoxComponentProps?: Record<string, unknown>;
  disabled?: boolean;
  itemClass?: string;
  label?: React.ReactNode;
  name?: string;
  onChange?: (v: Option[]) => void;
  options: Option[];
  value?: Option[];
  wrapClass?: string;
  wrapView?: CheckBoxWrapViews;
}

function getOptionLabel(
  option: Option,
  t: TFunction<'translation', undefined>,
) {
  if (option?.labelKey) return t(option.labelKey);
  return _get(option, 'label', '') as string;
}

const CheckBoxGroup = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: CheckBoxGroupProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
      CheckBoxComponent = CheckBox,
      checkBoxComponentProps,
      disabled,
      itemClass,
      label,
      name: fieldName,
      onChange,
      options = [],
      value: fieldValue,
      wrapClass,
      wrapView = CheckBoxWrapViews.list,
      ...rest
    } = props;
    const { t } = useTranslation();
    const hasLabel = !!label && typeof label === 'string';
    const hasCustomLabel = !!label && typeof label !== 'string';

    const preparedOptions = useMemo(
      () =>
        options.map((option) => ({
          ...option,
          label: getOptionLabel(option, t),
        })),
      [options, t],
    );

    const onChangeHandler = (v: Option) => {
      if (onChange) {
        const exists = _has(v, 'id')
          ? _some(fieldValue, ['id', v.id])
          : _some(fieldValue, ['value', v.value]);
        let newValue: Option[];
        if (exists) {
          newValue = _filter(fieldValue, (item) => item.value !== v.value);
        } else {
          newValue = Array.isArray(fieldValue) ? [...fieldValue, v] : [v];
        }
        onChange(newValue);
      }
    };

    const isOptionChecked = (option: Option) => {
      if (Array.isArray(fieldValue)) {
        if (_has(option, 'id')) {
          return _some(fieldValue, { id: option.id });
        }
        return _some(fieldValue, { value: option.value });
      }
      return false;
    };

    return (
      <div
        className={classNames(styles.groupRoot, {
          [styles.rootWithLabel]: hasLabel,
        })}
      >
        {hasLabel && <span className={styles.groupLabel}>{label}</span>}
        {hasCustomLabel && label}
        <div
          className={classNames(wrapClass, {
            [styles.defaultList]: wrapView === CheckBoxWrapViews.list,
            [styles.defaultRow]: wrapView === CheckBoxWrapViews.row,
            [styles.calendarRow]: wrapView === CheckBoxWrapViews.calendar,
            [styles.monthListRow]: wrapView === CheckBoxWrapViews.monthList,
          })}
        >
          {preparedOptions.map((option) => {
            const { value, label: optionLabel, id } = option;
            return (
              <div className={itemClass} key={uuidv4()}>
                <CheckBoxComponent
                  {...rest}
                  {...checkBoxComponentProps}
                  checked={isOptionChecked(option)}
                  id={id ? `${fieldName}-${id}` : `${fieldName}-${value}`}
                  label={optionLabel}
                  name={fieldName}
                  onChange={() => onChangeHandler(option)}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

export default memo(CheckBoxGroup);
